// App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation
} from "react-router-dom";
import HomePage from "./Pages/HomePage";
import ServicesPage from "./Pages/ServicesPage";
import AboutUs from "./Pages/AboutUs";
import ContactUs from "./Pages/ContactUs";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import DeleteBusinessUser from "./Pages/DeleteBusinessUser"; // استيراد صفحة حذف الحساب التجاري
import DeleteEndUser from "./Pages/DeleteEndUser"; // استيراد صفحة حذف الحساب العادي
import ReferralRedirect from "./Pages/ReferralRedirect";
import AdRedirect from "./Pages/AdRedirect";
import ProfileRedirect from "./Pages/ProfileRedirect";
import ReelRedirect from "./Pages/ReelRedirect";
import StoryRedirect from "./Pages/StoryRedirect";
import SocialProfileRedirect from "./Pages/SocialProfileRedirect";
import NotFound from "./Pages/NotFound";
import PostRedirect from "./Pages/PostRedirect";
import DownloadUser from "./Pages/DownloadUser";
import DownloadBusiness from "./Pages/DownloadBusiness";

const TABS = [
  { name: "Home", page: "home" },
  { name: "Services", page: "services" },
  { name: "About Us", page: "about" },
  { name: "Contact Us", page: "contact" },
  { name: "Privacy Policy", page: "privacy" }
];

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPage =
    location.pathname === "/delete-business"
      ? "deleteBusinessUser"
      : location.pathname === "/delete-enduser"
        ? "deleteEndUser"
        : location.pathname.slice(1) || "home";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navigateTo = (page) => {
    if (page === "deleteBusinessUser") {
      navigate("/delete-business");
    } else if (page === "deleteEndUser") {
      navigate("/delete-enduser");
    } else {
      navigate(`/${page === "home" ? "" : page}`);
    }
  };

  // These pages open on a dark hero, so the header floats transparently over
  // it until the user scrolls (or opens the mobile menu).
  const darkHero = ["home", "services", "about", "contact", "privacy"].includes(currentPage);
  const solid = !darkHero || scrolled || menuOpen;

  return (
    <div className="min-h-screen bg-[#FAF8F2] text-[#1E1B16]">
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
          solid
            ? "border-[#E7DDCF] bg-[#FAF8F2]/85 shadow-sm backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 md:px-10">
          <button
            onClick={() => navigateTo("home")}
            className="group flex items-center rounded-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
            aria-label="Mondoway home"
          >
            <img
              src="/icons/new_login_logo.svg"
              alt="Mondoway"
              className={`h-10 w-auto transition-all duration-500 group-hover:opacity-80 md:h-11 ${
                solid ? "" : "brightness-0 invert"
              }`}
            />
          </button>

          <div className="hidden items-center gap-1 md:flex">
            {TABS.map((tab) => (
              <button
                key={tab.page}
                onClick={() => navigateTo(tab.page)}
                aria-current={currentPage === tab.page ? "page" : undefined}
                className={`rounded-lg px-3.5 py-2 text-[15px] font-medium transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 ${
                  solid
                    ? currentPage === tab.page
                      ? "bg-[#F4E8FF] text-[#6A0DAD]"
                      : "text-[#4D4B46] hover:bg-[#F4E8FF]/70 hover:text-[#6A0DAD]"
                    : currentPage === tab.page
                      ? "bg-white/15 text-white"
                      : "text-white/75 hover:bg-white/10 hover:text-white"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <div className="hidden md:block">
            <button
              onClick={() => navigateTo("contact")}
              className={`rounded-xl px-5 py-2.5 text-[15px] font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-4 ${
                solid
                  ? "bg-[#6A0DAD] text-white shadow-sm shadow-[#6A0DAD]/20 hover:bg-[#4C087D] focus-visible:ring-[#6A0DAD]/25"
                  : "bg-white text-[#3B0764] shadow-lg shadow-black/20 hover:bg-[#F4E8FF] focus-visible:ring-white/40"
              }`}
            >
              Get in touch
            </button>
          </div>

          <button
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`rounded-xl border p-2.5 transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20 md:hidden ${
              solid
                ? "border-[#E7DDCF] bg-white text-[#1E1B16] hover:border-[#D8B4FE] hover:text-[#6A0DAD]"
                : "border-white/25 bg-white/10 text-white backdrop-blur"
            }`}
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              {menuOpen ? (
                <path d="M6 6l12 12M18 6 6 18" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </nav>

        {menuOpen && (
          <div className="border-t border-[#E7DDCF] bg-[#FAF8F2] px-6 pb-5 pt-3 md:hidden">
            <div className="flex flex-col gap-1">
              {TABS.map((tab) => (
                <button
                  key={tab.page}
                  onClick={() => navigateTo(tab.page)}
                  className={`rounded-xl px-4 py-3 text-left text-[15px] font-medium transition-colors duration-200 ${
                    currentPage === tab.page
                      ? "bg-[#F4E8FF] text-[#6A0DAD]"
                      : "text-[#4D4B46] hover:bg-[#F4E8FF]/70 hover:text-[#6A0DAD]"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => navigateTo("contact")}
              className="mt-3 w-full rounded-xl bg-[#6A0DAD] px-5 py-3 text-[15px] font-semibold text-white transition-colors duration-300 hover:bg-[#4C087D]"
            >
              Get in touch
            </button>
          </div>
        )}
      </header>

      {/* Dark-hero pages supply their own top spacing so they can sit under
          the transparent header; every other page needs clearance for it. */}
      <main className={darkHero ? "" : "pt-20"}>
        <Routes>
          <Route path="/" element={<HomePage onNavigate={navigateTo} />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/delete-business" element={<DeleteBusinessUser />} />
          <Route path="/delete-enduser" element={<DeleteEndUser />} />
          <Route path="/download-user" element={<DownloadUser />} />
          <Route path="/download-business" element={<DownloadBusiness />} />
          <Route path="/referal" element={<ReferralRedirect />} />
          <Route path="/referral" element={<ReferralRedirect />} />
          <Route path="/ad/:adId" element={<AdRedirect />} />
          <Route path="/posts" element={<PostRedirect />} />
          <Route path="/posts/:postId" element={<PostRedirect />} />
          <Route path="/stories" element={<StoryRedirect />} />
          <Route path="/stories/:storyId" element={<StoryRedirect />} />
          <Route path="/u" element={<SocialProfileRedirect />} />
          <Route path="/u/:handle" element={<SocialProfileRedirect />} />
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/profile/:profileId" element={<ProfileRedirect />} />
          <Route path="/reels" element={<ReelRedirect />} />
          <Route path="/reels/:reelId" element={<ReelRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <SiteFooter navigateTo={navigateTo} />
    </div>
  );
}

function SiteFooter({ navigateTo }) {
  const columns = [
    {
      heading: "Platform",
      links: [
        { name: "Home", page: "home" },
        { name: "Services", page: "services" },
        { name: "About Us", page: "about" }
      ]
    },
    {
      heading: "Support",
      links: [
        { name: "Contact Us", page: "contact" },
        { name: "Privacy Policy", page: "privacy" }
      ]
    },
    {
      heading: "Account",
      links: [
        { name: "Delete business account", page: "deleteBusinessUser" },
        { name: "Delete user account", page: "deleteEndUser" }
      ]
    }
  ];

  return (
    <footer className="border-t border-[#E7DDCF] bg-white">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <img src="/icons/new_login_logo.svg" alt="Mondoway" className="h-11 w-auto" />
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-[#4D4B46]">
              Jordan&rsquo;s first all-in-one smart platform — shopping, travel,
              food delivery and a trusted marketplace, together in one app.
            </p>
          </div>

          {columns.map((column) => (
            <div key={column.heading} className="md:col-span-2">
              <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E1B16]">
                {column.heading}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.page}>
                    <button
                      onClick={() => navigateTo(link.page)}
                      className="text-left text-[15px] text-[#4D4B46] transition-colors duration-200 hover:text-[#6A0DAD]"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 border-t border-[#E7DDCF] pt-7">
          <p className="text-sm text-[#7A6F61]">
            © {new Date().getFullYear()} Mondoway. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Navigation />
    </BrowserRouter>
  );
}

export default App;
